import * as THREE from 'three';
import { Capsule } from 'three/addons/math/Capsule.js';

const STAND_HEIGHT = 1.72;
const CROUCH_HEIGHT = 1.05;
const RADIUS = 0.34;

/**
 * How long you may stand somewhere the map's clip hull forbids before being
 * put back.
 *
 * Three seconds, not one. The hull disagrees with legitimate play at a handful
 * of marginal spots per map (see ClipHull.forbidsStanding), and the residual
 * does not shrink with a stricter test — so the defence is that a false
 * positive has to persist for three unbroken seconds of standing still to cost
 * anything, while the case that matters, falling out of the world, is caught
 * instantly by a separate rule that cannot misfire.
 */
const OUT_OF_BOUNDS_GRACE = 3.0;

/**
 * The capsule's shape, exported because anything that reads `pc.position` off
 * the wire has to undo it.
 *
 * `position` is `capsule.end` — the EYE — which sits `height - RADIUS` above
 * the feet, not `height`. Both the network body placement and the server's
 * hitboxes had 1.62 hard-coded here, which is neither: it buried remote players
 * 0.24 m into the floor and dropped every hitbox by the same amount. Deriving
 * it from the live `height` also makes crouch correct for free.
 */
export const CAPSULE_RADIUS = RADIUS;
export const CAPSULE_STAND_HEIGHT = STAND_HEIGHT;
/** Feet-to-eye distance for a capsule of total height `h`. */
export const eyeOffset = (h = STAND_HEIGHT) => h - RADIUS;

const _forward = new THREE.Vector3();
const _right = new THREE.Vector3();
const _wish = new THREE.Vector3();
const _stepS = new THREE.Vector3();
const _stepE = new THREE.Vector3();
const _stepV = new THREE.Vector3();
const _preS = new THREE.Vector3();
const _preE = new THREE.Vector3();
const _postS = new THREE.Vector3();
const _postE = new THREE.Vector3();
const _flat = new THREE.Vector3();
const _mFwd = new THREE.Vector3();
const _mOrigin = new THREE.Vector3();
const _mProbe = new THREE.Vector3();
const _mTarget = new THREE.Vector3();
const _mFrom = new THREE.Vector3();
const _mS = new THREE.Vector3();
const _mE = new THREE.Vector3();
const _DOWN = new THREE.Vector3(0, -1, 0);

/**
 * Source-engine flavoured character controller: acceleration + friction rather
 * than direct velocity assignment, which is what gives CoD/Source movement its
 * weight. Camera shake, bob and recoil are layered on top as additive offsets.
 */
export class PlayerController {
  /**
   * Seconds of immunity after respawning. Long enough to get out of the open,
   * short enough that it cannot be used to trade a fight. Ends early on firing.
   */
  static SPAWN_PROTECT = 2.0;

  constructor(physics, input, camera) {
    this.physics = physics;
    this.input = input;
    this.camera = camera;

    this.capsule = new Capsule(
      new THREE.Vector3(0, RADIUS, 0),
      new THREE.Vector3(0, STAND_HEIGHT - RADIUS, 0),
      RADIUS
    );
    this.velocity = new THREE.Vector3();
    this.yaw = 0;
    this.pitch = 0;
    this.grounded = false;
    this.height = STAND_HEIGHT;
    this.crouching = false;
    this.sprinting = false;
    this.sliding = false;
    this.slideTimer = 0;
    this.coyote = 0;
    this.jumpBuffer = 0;

    /**
     * The last place this player stood on solid ground, and the recovery that
     * uses it. See `_recoverIfLost`.
     */
    this.safeGround = new THREE.Vector3(0, RADIUS, 0);
    this.safeYaw = 0;
    /** Counts recoveries, so a probe can assert on them. */
    this.recoveries = 0;
    /** Seconds spent standing somewhere the map's own clip hull forbids. */
    this.outOfBoundsFor = 0;
    /** Whether the hull forbids the spot under our feet right now. */
    this.forbiddenHere = false;
    /** Whether the last ground we stood on was forbidden. Drives the fall rule. */
    this.lastGroundForbidden = false;
    /** Fired when the player is pulled back into the world. */
    this.onRecover = null;

    this.speeds = { walk: 4.4, sprint: 7.6, crouch: 2.3, air: 1.6 };
    this.accel = { ground: 90, air: 14 };
    this.friction = 11;

    // Additive camera state
    this.bobPhase = 0;
    this.bob = new THREE.Vector3();
    this.roll = 0;
    this.recoil = new THREE.Vector2();   // current view punch
    this.recoilVel = new THREE.Vector2();
    this.trauma = 0;                     // 0..1 for explosion shake
    this.eyeOffset = 0;

    this.health = 100;
    this.maxHealth = 100;
    this.regenDelay = 0;
    this.alive = true;
    this.onDeath = null;
    this.deathTime = 0;      // seconds since death, drives the camera fall
    this.spawnProtect = 0;
    this.deathDir = null;

    this.onLadder = false;
    this.ladderCooldown = 0;
    this.platform = null;
    // How high a lip the player walks straight over. 0.55 m clears kerbs,
    // stair treads, debris and sandbags without letting them stroll onto
    // waist-high cover, which should still need a jump or a mantle.
    this.stepHeight = 0.55;
    // Mantle: how far forward we look for a ledge, and the tallest one we can
    // pull up onto. 1.6 m is roughly shoulder height — waist-high cover, crates
    // and low walls yes; a 2.6 m shipping container no.
    this.mantleReach = 1.05;
    this.mantleMax = 1.60;
    this.mantle = null;
  }

  get position() { return this.capsule.end; }

  /**
   * Step offset — walking up onto low obstacles without jumping.
   *
   * A swept capsule against a triangle soup has no concept of a "step": a kerb,
   * a stair tread or a piece of debris is just a wall it slides along, which is
   * why the player could not get onto ANYTHING, even a 24 cm kerb. Every FPS
   * solves this the same way: when a grounded move is blocked, retry it lifted
   * by the step height and then drop back down onto whatever is there.
   *
   * @param delta horizontal movement that was blocked
   * @returns true if the capsule ended up standing higher than it started
   */
  _tryStepUp(delta) {
    const c = this.capsule;
    const startY = c.start.y;
    const sS = _stepS.copy(c.start);
    const sE = _stepE.copy(c.end);
    const restore = () => { c.start.copy(sS); c.end.copy(sE); };

    // 1. Lift. If we are already inside something up there, this is a wall.
    c.translate(_stepV.set(0, this.stepHeight, 0));
    if (this.physics.overlaps(c)) { restore(); return false; }

    // 2. Move horizontally at the raised height.
    c.translate(delta);
    if (this.physics.overlaps(c)) { restore(); return false; }

    // 3. Drop back down onto the surface. If nothing is found within the step
    //    height it was a gap rather than a step, so refuse — otherwise the
    //    player would float across holes.
    const STEP = 0.04;
    let dropped = 0;
    while (dropped < this.stepHeight + STEP) {
      c.translate(_stepV.set(0, -STEP, 0));
      dropped += STEP;
      if (this.physics.overlaps(c)) {
        c.translate(_stepV.set(0, STEP, 0));      // back off the penetration
        if (c.start.y - startY > 0.01) return true;
        restore();
        return false;
      }
    }
    restore();
    return false;
  }

  /**
   * Mantle / vault.
   *
   * Clearing an obstacle is not the same as getting onto it. A sprint-jump at
   * waist-high cover already peaks well above it (measured 1.32 m against a
   * 1.055 m barrier) but the capsule is moving forward at the apex, so the
   * collider pushes it back off the lip and the player slides down the face —
   * blocked on 4 of 5 attempts. Every modern shooter solves this with an
   * explicit mantle rather than by tuning jump height.
   *
   * Detection is two casts: forward at chest height to find a wall, then down
   * from beyond-and-above it to find the top. The destination is validated with
   * a real capsule overlap test so the player can never mantle into geometry.
   */
  _tryMantle() {
    if (this.mantle) return false;
    const c = this.capsule;
    const feetY = c.start.y - c.radius;

    _mFwd.set(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));

    // 1. Is there a wall in front of us at chest height?
    _mOrigin.set(c.start.x, feetY + 0.85, c.start.z);
    const wall = this.physics.raycast(_mOrigin, _mFwd, this.mantleReach);
    if (!wall) { this._mWhy = 'no wall within reach'; return false; }
    // Must be a wall, not a floor or an overhang.
    if (Math.abs(wall.normal.y) > 0.45) { this._mWhy = `wall normal.y=${wall.normal.y.toFixed(2)}`; return false; }

    // 2. Find its top: from above and past the face, cast down.
    //
    // Probe several depths rather than one. A single sample at radius+0.12
    // overshoots anything thin — a low wall, a railing, a crate edge — landing
    // beyond the far side where there is no floor, which reported "no top" on
    // ledges that were perfectly mantle-able. Nearest valid surface wins.
    let top = null;
    let rise = 0;
    for (const depth of [0.18, 0.34, 0.52, 0.75]) {
      _mProbe.copy(_mOrigin).addScaledVector(_mFwd, wall.distance + depth);
      _mProbe.y = feetY + this.mantleMax + 0.5;
      const hit = this.physics.raycast(_mProbe, _DOWN, this.mantleMax + 1.0);
      if (!hit) continue;
      const r = hit.point.y - feetY;
      if (r < 0.30 || r > this.mantleMax) continue;
      if (hit.normal.y < 0.5) continue;          // still a wall, not a top
      top = hit; rise = r;
      break;
    }
    if (!top) { this._mWhy = 'no usable top within reach'; return false; }

    // Stand far enough in that the capsule is over solid ground, not the lip.
    _mProbe.copy(_mOrigin).addScaledVector(_mFwd, wall.distance + c.radius + 0.10);

    // 3. Validate the landing with a real capsule test.
    _mS.copy(c.start); _mE.copy(c.end);
    const h = c.end.y - c.start.y;
    c.start.set(_mProbe.x, top.point.y + c.radius + 0.02, _mProbe.z);
    c.end.set(_mProbe.x, top.point.y + c.radius + 0.02 + h, _mProbe.z);
    const blocked = this.physics.overlaps(c);
    _mTarget.copy(c.start);
    c.start.copy(_mS); c.end.copy(_mE);
    if (blocked) { this._mWhy = 'destination blocked'; return false; }

    this.mantle = {
      t: 0,
      dur: THREE.MathUtils.clamp(0.22 + rise * 0.16, 0.24, 0.5),
      from: _mFrom.copy(c.start).clone(),
      to: _mTarget.clone(),
      h,
    };
    this.velocity.set(0, 0, 0);
    this.onMantle?.(rise);
    return true;
  }

  /** Drive an in-progress mantle. Returns true while it owns the movement. */
  _updateMantle(dt) {
    const m = this.mantle;
    if (!m) return false;
    m.t += dt;
    const k = THREE.MathUtils.clamp(m.t / m.dur, 0, 1);
    // Up first, then forward — that is the shape of a real pull-up, and it
    // stops the capsule scraping through the lip on the way.
    const up = Math.min(1, k / 0.55);
    const fwd = THREE.MathUtils.smoothstep(k, 0.35, 1);
    const c = this.capsule;
    c.start.set(
      THREE.MathUtils.lerp(m.from.x, m.to.x, fwd),
      THREE.MathUtils.lerp(m.from.y, m.to.y, up * up * (3 - 2 * up)),
      THREE.MathUtils.lerp(m.from.z, m.to.z, fwd)
    );
    c.end.copy(c.start);
    c.end.y += m.h;
    if (k >= 1) {
      this.mantle = null;
      this.grounded = true;
      this.velocity.set(0, 0, 0);
    }
    return true;
  }

  teleport(x, y, z) {
    this.capsule.start.set(x, y + RADIUS, z);
    this.capsule.end.set(x, y + this.height - RADIUS, z);
    this.velocity.set(0, 0, 0);
    // A teleport is by definition somewhere legitimate, so it also becomes the
    // fallback for a recovery that happens before the player has stood anywhere.
    this.safeGround.copy(this.capsule.start);
  }

  /**
   * Pull the player back if they have left the world.
   *
   * Both GoldSrc maps lost their CLIP brushes in conversion (see
   * `Physics._measureBounds`), so roofs the original level fenced off are
   * walkable here, and running off one drops you through nothing forever — the
   * bug as reported: "you'll be floating down through textures infinitely".
   *
   * The recovery is deliberately the mildest thing that works: back to the last
   * ground you stood on, which is where the missing invisible wall would have
   * kept you anyway. Not death — falling out is the level's fault, not the
   * player's, and a death would hand the other side a free point. Not a
   * respawn either, which post-plant would be a much bigger gift than the bug.
   *
   * It runs identically on the client and the server because both drive this
   * same controller, so prediction agrees with the authority instead of
   * fighting it.
   */
  _recoverIfLost(dt = 0) {
    if (!this._lost(dt)) return false;
    this.capsule.start.copy(this.safeGround);
    this.capsule.end.copy(this.safeGround);
    this.capsule.end.y += Math.max(0.01, this.height - RADIUS * 2);
    this.velocity.set(0, 0, 0);
    this.grounded = true;
    this.mantle = null;
    this.sliding = false;
    this.recoveries++;
    this.outOfBoundsFor = 0;
    this.onRecover?.();
    return true;
  }

  /**
   * Two ways to be somewhere you should not be.
   *
   * The first is the backstop: below every triangle in the world, which can
   * only mean the fall never ends.
   *
   * The second is the map's own answer. GoldSrc maps carry a standing-player
   * collision hull that INCLUDES the clip brushes the face lump throws away, so
   * it knows the roof near A was fenced off even though our geometry does not.
   * That catches the problem where it starts — the moment you are standing
   * somewhere the level forbade — instead of a hundred metres below the map
   * after the fall has already happened.
   *
   * It is deliberately slow to fire. `forbidsStanding` measured zero false
   * positives over 8654 grounded samples (tools/hull-probe.mjs), but "zero
   * measured" is not "impossible", and being yanked mid-fight for standing
   * somewhere legal is far worse than the bug. Requiring a continuous second on
   * the ground means a brush past a bad spot costs nothing, and a player who
   * has genuinely climbed somewhere out of bounds still gets put back before
   * they can do anything with it.
   */
  _lost(dt) {
    const killY = this.physics.killY;
    if (killY != null && this.capsule.start.y <= killY) return true;

    const hull = this.physics.clipHull;
    if (!hull) return false;

    if (this.grounded) {
      this.lastGroundForbidden = this.forbiddenHere = hull.forbidsStanding(
        this.capsule.start.x, this.capsule.start.y - RADIUS, this.capsule.start.z);
      this.outOfBoundsFor = this.forbiddenHere ? (this.outOfBoundsFor ?? 0) + dt : 0;
      return this.outOfBoundsFor >= OUT_OF_BOUNDS_GRACE;
    }

    this.outOfBoundsFor = 0;
    // Already falling, and the ground you left was fenced off. There is nothing
    // to wait for: this is the reported bug — "you'll be floating down through
    // textures infinitely" — happening, and catching it here rather than 25 m
    // below the map is the difference between a stumble and a lost round. It
    // also cannot misfire on somebody standing anywhere legal, because they are
    // not falling and their last ground was not fenced.
    return this.lastGroundForbidden === true && this.velocity.y < -6;
  }

  addRecoil(pitchKick, yawKick) {
    this.recoilVel.x += pitchKick;
    this.recoilVel.y += yawKick;
  }

  addTrauma(amount) {
    this.trauma = Math.min(1, this.trauma + amount);
  }

  /**
   * Apply damage. Returns true if this killed the player.
   *
   * Extra args are accepted and ignored so the signature matches
   * `Enemy.damage(amount, part, dir)` — that lets one hitscan code path damage
   * either a bot or the player without branching on the victim's type.
   */
  damage(amount, part, dir) {
    if (!this.alive) return false;
    // Spawn protection. On the BSP maps the authored spawn points are tight
    // clusters — Dust II's best available spawn is 6 m from a bot and the
    // median is under 3 m — so no amount of clever spawn PICKING can guarantee
    // room. A short grace period is what actually stops respawning into a
    // firefight and dying before the fade finishes. It ends the moment the
    // player attacks, so it can never be used offensively.
    if (this.spawnProtect > 0) return false;
    this.health = Math.max(0, this.health - amount);
    this.regenDelay = 4.5;
    this.addTrauma(Math.min(0.5, amount / 60));
    if (this.health === 0) {
      this.alive = false;
      this.deathTime = 0;
      this.deathDir = dir ? dir.clone?.() ?? null : null;
      this.onDeath?.(dir);
      return true;
    }
    return false;
  }

  /** Put the player back in the fight at `pos` (a floor position). */
  respawn(pos, yaw = this.yaw) {
    this.alive = true;
    this.health = this.maxHealth;
    this.regenDelay = 0;
    this.trauma = 0;
    this.deathTime = 0;
    this.mantle = null;
    this.onLadder = false;
    this.velocity.set(0, 0, 0);
    if (pos) this.teleport(pos.x, pos.y, pos.z);
    this.yaw = yaw;
    this.pitch = 0;
    this.spawnProtect = PlayerController.SPAWN_PROTECT;
  }

  /** Drop spawn protection the instant the player takes a shot. */
  endSpawnProtection() { this.spawnProtect = 0; }

  update(dt, opts = {}) {
    const input = this.input;
    if (this.spawnProtect > 0) this.spawnProtect = Math.max(0, this.spawnProtect - dt);

    // ---- look -------------------------------------------------------------
    this.yaw -= input.mouse.dx * (opts.aimSensitivityScale ?? 1);
    this.pitch -= input.mouse.dy * (opts.aimSensitivityScale ?? 1);
    this.pitch = THREE.MathUtils.clamp(this.pitch, -Math.PI / 2 + 0.02, Math.PI / 2 - 0.02);

    // Spring the recoil punch back toward zero.
    this.recoil.x += this.recoilVel.x * dt;
    this.recoil.y += this.recoilVel.y * dt;
    this.recoilVel.multiplyScalar(Math.exp(-9 * dt));
    this.recoil.multiplyScalar(Math.exp(-6.5 * dt));

    // ---- dead -------------------------------------------------------------
    // Look still works — being able to watch the rest of the round is the whole
    // point of dying in a round-based mode — but nothing else does. The camera
    // drops to the deck and rolls, which reads as collapsing without needing a
    // ragdoll or a corpse mesh.
    if (!this.alive) {
      this.deathTime += dt;
      this._applyCamera(dt, opts);
      return;
    }

    // ---- mantle -----------------------------------------------------------
    // An active mantle owns the capsule outright: no gravity, no input, no
    // collision resolution, because the destination was already validated.
    if (this.mantle) {
      this._updateMantle(dt);
      this._applyCamera(dt, opts);
      return;
    }

    // ---- stance -----------------------------------------------------------
    const wantsCrouch = input.down('ControlLeft') || input.down('KeyC');
    const moving2d = Math.hypot(this.velocity.x, this.velocity.z);
    const wantsSprint = input.down('ShiftLeft') && !wantsCrouch && moving2d > 0.5 && input.down('KeyW');

    if (wantsCrouch && this.sprinting && moving2d > 6 && !this.sliding && this.grounded) {
      this.sliding = true;
      this.slideTimer = 0.75;
      this.velocity.multiplyScalar(1.28);
    }
    if (this.sliding) {
      this.slideTimer -= dt;
      if (this.slideTimer <= 0 || !this.grounded) this.sliding = false;
    }
    this.crouching = wantsCrouch || this.sliding;
    this.sprinting = wantsSprint && !this.crouching;

    const targetHeight = this.crouching ? CROUCH_HEIGHT : STAND_HEIGHT;
    this.height = THREE.MathUtils.damp(this.height, targetHeight, 14, dt);
    this.capsule.end.copy(this.capsule.start).y += Math.max(0.01, this.height - RADIUS * 2);

    // ---- wish direction ---------------------------------------------------
    _forward.set(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
    _right.set(Math.cos(this.yaw), 0, -Math.sin(this.yaw));
    _wish.set(0, 0, 0);
    // Frozen (round freeze time): look works, movement does not.
    if (opts.frozen) {
      this.velocity.x = 0;
      this.velocity.z = 0;
    } else {
    if (input.down('KeyW')) _wish.add(_forward);
    if (input.down('KeyS')) _wish.sub(_forward);
    if (input.down('KeyD')) _wish.add(_right);
    if (input.down('KeyA')) _wish.sub(_right);
    }
    if (_wish.lengthSq() > 0) _wish.normalize();

    let maxSpeed = this.sprinting ? this.speeds.sprint
      : this.crouching ? this.speeds.crouch
      : this.speeds.walk;
    if (opts.ads) maxSpeed *= 0.52;
    if (this.sliding) maxSpeed = this.speeds.sprint * 1.15;

    // ---- acceleration -----------------------------------------------------
    if (this.grounded) {
      if (!this.sliding) {
        const speed = Math.hypot(this.velocity.x, this.velocity.z);
        if (speed > 0.001) {
          const drop = Math.max(speed, 3.0) * this.friction * dt;
          const scale = Math.max(0, speed - drop) / speed;
          this.velocity.x *= scale;
          this.velocity.z *= scale;
        }
      } else {
        this.velocity.x *= Math.exp(-1.6 * dt);
        this.velocity.z *= Math.exp(-1.6 * dt);
      }
      this._accelerate(_wish, maxSpeed, this.sliding ? 6 : this.accel.ground, dt);
    } else {
      this._accelerate(_wish, this.speeds.air * 2.4, this.accel.air, dt);
    }

    // ---- ladders ----------------------------------------------------------
    // Checked before gravity so climbing is a genuine movement mode rather than
    // a fight against falling. Look direction drives the climb, the way it does
    // in Half-Life: aim up the ladder and hold forward.
    const wasOnLadder = this.onLadder;
    this.onLadder = this.physics.ladders?.length
      ? this.physics.onLadder(this.capsule) : false;
    if (this.onLadder && this.input.pressed('Space')) {
      // Kick off the ladder.
      this.onLadder = false;
      this.ladderCooldown = 0.35;
      this.velocity.copy(_forward).multiplyScalar(-3.4);
      this.velocity.y = 5.2;
    }
    if (this.ladderCooldown > 0) { this.ladderCooldown -= dt; this.onLadder = false; }

    if (this.onLadder) {
      const climb = 3.1;
      let along = 0;
      if (input.down('KeyW')) along += 1;
      if (input.down('KeyS')) along -= 1;
      // Pitch decides whether "forward" means up or down the rungs.
      const vertical = Math.sin(this.pitch);
      this.velocity.y = along * climb * (Math.abs(vertical) > 0.25 ? Math.sign(vertical) : 1);
      // Lateral control comes from STRAFE only. Deriving it from the full wish
      // vector meant holding forward while facing away from the ladder slid you
      // off it sideways — W/S is the climb axis here, not a walk axis.
      const strafe = (input.down('KeyD') ? 1 : 0) - (input.down('KeyA') ? 1 : 0);
      this.velocity.x = _right.x * strafe * 1.7;
      this.velocity.z = _right.z * strafe * 1.7;
      if (!wasOnLadder) this.onLadderEnter?.();
      this.grounded = false;
    }

    // ---- jump -------------------------------------------------------------
    if (this.input.pressed('Space')) this.jumpBuffer = 0.14;
    this.jumpBuffer -= dt;
    this.coyote = this.grounded ? 0.12 : this.coyote - dt;
    // Mantle takes priority over jumping: if you are looking at a ledge you can
    // pull up onto, pressing jump should put you on it.
    if (!this.onLadder && this.jumpBuffer > 0 && this._tryMantle()) {
      this.jumpBuffer = 0;
      this._applyCamera(dt, opts);
      return;
    }
    if (!this.onLadder && this.jumpBuffer > 0 && this.coyote > 0) {
      this.velocity.y = 7.4;
      this.jumpBuffer = 0;
      this.coyote = 0;
      this.grounded = false;
      this.sliding = false;
      this.onJump?.();
    }

    if (!this.onLadder) this.velocity.y -= this.physics.gravity * dt;

    // ---- integrate + collide ---------------------------------------------
    const step = this.velocity.clone().multiplyScalar(dt);
    _preS.copy(this.capsule.start);
    _preE.copy(this.capsule.end);
    this.capsule.translate(step);

    // Ride movers: apply the platform's own motion before resolving, so a
    // rising lift carries the player instead of clipping through them.
    if (this.platform?.delta) this.capsule.translate(this.platform.delta);

    const wasGrounded = this.grounded;
    const res = this.physics.collideCapsule(this.capsule, this.velocity);
    this.grounded = res.grounded;
    this.platform = res.platform ?? null;

    // Step offset. If a grounded move was mostly stopped by something, retry it
    // as a step before accepting the slide. Without this the capsule treats a
    // kerb exactly like a wall.
    const wantH = Math.hypot(step.x, step.z);
    if (!this.onLadder && wantH > 1e-4 && (wasGrounded || this.coyote > 0)) {
      const gotH = Math.hypot(this.capsule.start.x - _preS.x, this.capsule.start.z - _preS.z);
      if (gotH < wantH * 0.72) {
        _postS.copy(this.capsule.start);
        _postE.copy(this.capsule.end);
        this.capsule.start.copy(_preS);
        this.capsule.end.copy(_preE);
        if (this._tryStepUp(_flat.set(step.x, 0, step.z))) {
          this.grounded = true;
          if (this.velocity.y < 0) this.velocity.y = 0;
        } else {
          // No step available — keep the slide the collider already produced.
          this.capsule.start.copy(_postS);
          this.capsule.end.copy(_postE);
        }
      }
    }
    if (!wasGrounded && this.grounded) {
      const impact = Math.min(1, Math.abs(step.y / dt) / 18);
      if (impact > 0.25) { this.addTrauma(impact * 0.25); this.onLand?.(impact); }
    }

    // Remember where solid ground was, then check we are still in the world.
    //
    // Recorded only while grounded and stationary in Y, so a frame spent
    // clipping through a roof is never mistaken for somewhere safe to return —
    // and, now that the map's own hull can say so, never on ground the level
    // fenced off. Without that second condition a recovery from a fenced roof
    // put you straight back on the fenced roof, which is not a recovery.
    //
    // The order matters: `_recoverIfLost` runs the hull test and leaves the
    // answer in `forbiddenHere`, so this reads the flag AFTER it is up to date
    // rather than paying for a second query.
    const lost = this._recoverIfLost(dt);
    if (!lost && this.grounded && Math.abs(this.velocity.y) < 0.5
      && this.forbiddenHere !== true) {
      this.safeGround.copy(this.capsule.start);
      this.safeYaw = this.yaw;
    }

    this._applyCamera(dt, opts);

    // ---- regen ------------------------------------------------------------
    this.regenDelay -= dt;
    if (this.regenDelay <= 0 && this.health < this.maxHealth) {
      this.health = Math.min(this.maxHealth, this.health + 26 * dt);
    }
  }

  /** Camera placement: eye height, bob, lean, recoil and shake. */
  _applyCamera(dt, opts = {}) {
    // ---- camera feel ------------------------------------------------------
    const hSpeed = Math.hypot(this.velocity.x, this.velocity.z);
    const bobAmount = this.grounded ? THREE.MathUtils.clamp(hSpeed / this.speeds.sprint, 0, 1) : 0;
    this.bobPhase += dt * (6.2 + hSpeed * 1.15);
    const bobScale = (opts.ads ? 0.25 : 1) * bobAmount;
    this.bob.x = Math.cos(this.bobPhase) * 0.035 * bobScale;
    this.bob.y = Math.sin(this.bobPhase * 2) * 0.026 * bobScale;
    this.bob.z = 0;

    // Lean into strafes; adds a lot of perceived weight.
    const strafe = _right.dot(this.velocity) / this.speeds.sprint;
    const targetRoll = -strafe * 0.035 + (this.sliding ? 0.09 : 0);
    this.roll = THREE.MathUtils.damp(this.roll, targetRoll, 8, dt);

    this.trauma = Math.max(0, this.trauma - dt * 1.4);
    const shake = this.trauma * this.trauma;
    const t = performance.now() * 0.001;
    const shakeX = (Math.sin(t * 47.3) + Math.sin(t * 31.7)) * 0.5 * shake * 0.05;
    const shakeY = (Math.sin(t * 39.1) + Math.sin(t * 53.9)) * 0.5 * shake * 0.05;

    this.eyeOffset = THREE.MathUtils.damp(this.eyeOffset, this.sliding ? -0.12 : 0, 12, dt);

    this.camera.position.copy(this.capsule.end);
    this.camera.position.y += RADIUS * 0.62 + this.eyeOffset;
    this.camera.position.x += this.bob.x;
    this.camera.position.y += this.bob.y;

    // Death: sink to deck height and roll onto one side over ~0.9 s. Cheapest
    // thing that reads as collapsing — no ragdoll, no corpse mesh needed.
    let deathRoll = 0;
    if (!this.alive) {
      const k = THREE.MathUtils.clamp(this.deathTime / 0.9, 0, 1);
      const ease = 1 - (1 - k) ** 3;
      const feetY = this.capsule.start.y - RADIUS;
      const eyeY = this.camera.position.y;
      this.camera.position.y = THREE.MathUtils.lerp(eyeY, feetY + 0.28, ease);
      deathRoll = ease * (this.deathDir ? Math.sign(this.deathDir.x || 1) : 1) * 1.15;
    }

    this.camera.rotation.set(
      this.pitch + this.recoil.x + shakeY,
      this.yaw + this.recoil.y + shakeX,
      this.roll + deathRoll,
      'YXZ'
    );
  }

  _accelerate(wishDir, maxSpeed, accel, dt) {
    const current = this.velocity.dot(wishDir);
    const add = maxSpeed - current;
    if (add <= 0) return;
    const accelSpeed = Math.min(accel * maxSpeed * dt, add);
    this.velocity.addScaledVector(wishDir, accelSpeed);
  }
}
